import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
} from "react";
import { authAPI } from "../services/api";
import toast from "react-hot-toast";

const AuthContext = createContext();

const getStoredPendingEmail = () => {
  if (typeof window === "undefined") {
    return null;
  }
  return localStorage.getItem("pendingVerificationEmail");
};

const initialState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  pendingVerificationEmail: getStoredPendingEmail(),
};

const authReducer = (state, action) => {
  switch (action.type) {
    case "SET_LOADING":
      return { ...state, isLoading: action.payload };

    case "LOGIN_SUCCESS":
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
      };

    case "LOGOUT":
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
      };

    case "UPDATE_USER":
      return {
        ...state,
        user: { ...state.user, ...action.payload },
      };

    case "SET_PENDING_VERIFICATION":
      return {
        ...state,
        pendingVerificationEmail: action.payload,
      };

    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const setPendingVerificationEmail = useCallback(
    (email) => {
      if (typeof window !== "undefined") {
        if (email) {
          localStorage.setItem("pendingVerificationEmail", email);
        } else {
          localStorage.removeItem("pendingVerificationEmail");
        }
      }

      dispatch({
        type: "SET_PENDING_VERIFICATION",
        payload: email || null,
      });
    },
    [dispatch],
  );

  // Initialize auth state on app load
  useEffect(() => {
    const initializeAuth = async () => {
      const token = localStorage.getItem("token");
      const user = localStorage.getItem("user");

      if (token && user) {
        try {
          // Verify token is still valid by fetching profile
          const profileData = await authAPI.getProfile();
          dispatch({
            type: "LOGIN_SUCCESS",
            payload: {
              user: profileData.user,
              token,
            },
          });
        } catch (error) {
          // Token is invalid, clear storage
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          dispatch({ type: "SET_LOADING", payload: false });
        }
      } else {
        dispatch({ type: "SET_LOADING", payload: false });
      }
    };

    initializeAuth();
  }, []);

  const login = async (credentials) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      const data = await authAPI.login(credentials);

      // Server requires a TOTP code before issuing the full session token.
      // Must be checked first — this response has no user object.
      if (data.requires2FA) {
        dispatch({ type: "SET_LOADING", payload: false });
        return {
          success: false,
          requires2FA: true,
          tempToken: data.tempToken,
        };
      }

      if (!data.user?.isEmailVerified) {
        setPendingVerificationEmail(data.user?.email || credentials.email);
        dispatch({ type: "SET_LOADING", payload: false });
        return {
          success: false,
          error: "Please verify your email before signing in.",
          requiresVerification: true,
          email: data.user?.email || credentials.email,
        };
      }

      setPendingVerificationEmail(null);

      // Store in localStorage
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      dispatch({
        type: "LOGIN_SUCCESS",
        payload: data,
      });

      toast.success("Connexion reussie !");
      return { success: true };
    } catch (error) {
      dispatch({ type: "SET_LOADING", payload: false });
      if (
        error.response?.status === 403 &&
        error.response?.data?.requiresVerification
      ) {
        const pendingEmail =
          error.response?.data?.email || credentials.email || null;
        setPendingVerificationEmail(pendingEmail);
        return {
          success: false,
          error:
            error.response?.data?.error ||
            "Please verify your email before signing in.",
          requiresVerification: true,
          email: pendingEmail,
        };
      }
      return {
        success: false,
        error: error.response?.data?.error || "Login failed",
      };
    }
  };

  const register = async (userData) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      const data = await authAPI.register(userData);
      const registeredEmail = data.user?.email || userData.email;

      if (!data.user?.isEmailVerified) {
        setPendingVerificationEmail(registeredEmail);
        dispatch({ type: "SET_LOADING", payload: false });
        toast.success(
          "Registration successful! Please verify your email to continue.",
        );
        return {
          success: true,
          requiresVerification: true,
          email: registeredEmail,
        };
      }

      setPendingVerificationEmail(null);

      // Store in localStorage
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      dispatch({
        type: "LOGIN_SUCCESS",
        payload: data,
      });

      toast.success("Inscription reussie !");
      return { success: true };
    } catch (error) {
      dispatch({ type: "SET_LOADING", payload: false });
      return {
        success: false,
        error: error.response?.data?.error || "Registration failed",
        details: error.response?.data?.details || null,
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setPendingVerificationEmail(null);
    dispatch({ type: "LOGOUT" });
    toast.success("Deconnexion reussie");
  };

  const updateUser = (userData) => {
    const updatedUser = { ...state.user, ...userData };
    localStorage.setItem("user", JSON.stringify(updatedUser));
    dispatch({ type: "UPDATE_USER", payload: userData });
  };

  const changePassword = async (passwordData) => {
    try {
      await authAPI.changePassword(passwordData);
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "Password change failed",
        details: error.response?.data?.details || null,
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      await authAPI.forgotPassword(email);
      toast.success("E-mail de reinitialisation envoye");
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || "Request failed",
      };
    }
  };

  const resetPassword = async (tokenData) => {
    try {
      await authAPI.resetPassword(tokenData);
      toast.success("Reinitialisation du mot de passe reussie");
      return { success: true };
    } catch (error) {
      const backendError = error.response?.data?.error;
      const details = error.response?.data?.details;
      const errorMessage =
        backendError === "Validation failed" && details?.length > 0
          ? details.map((d) => d.msg).join(". ")
          : backendError || "La reinitialisation du mot de passe a echoue";
      return { success: false, error: errorMessage };
    }
  };

  const loginWith2FA = async (tempToken, code) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      const data = await authAPI.twoFactorValidate(tempToken, code);

      setPendingVerificationEmail(null);
      localStorage.setItem("token", data.token);
      localStorage.setItem("user", JSON.stringify(data.user));

      dispatch({ type: "LOGIN_SUCCESS", payload: data });
      toast.success("Connexion reussie !");
      return { success: true };
    } catch (error) {
      dispatch({ type: "SET_LOADING", payload: false });
      return {
        success: false,
        error: error.response?.data?.error || "Invalid authentication code",
      };
    }
  };

  const resendVerification = useCallback(
    async (email) => {
      try {
        await authAPI.resendVerification(email);
        setPendingVerificationEmail(email);
        toast.success("E-mail de verification envoye");
        return { success: true };
      } catch (error) {
        return {
          success: false,
          error:
            error.response?.data?.error ||
            "Impossible d'envoyer l'e-mail de verification",
        };
      }
    },
    [setPendingVerificationEmail],
  );

  const verifyEmailToken = useCallback(
    async (token) => {
      try {
        const data = await authAPI.verifyEmail(token);
        setPendingVerificationEmail(null);
        if (data.token && data.user) {
          localStorage.setItem("token", data.token);
          localStorage.setItem("user", JSON.stringify(data.user));
          dispatch({
            type: "LOGIN_SUCCESS",
            payload: {
              token: data.token,
              user: data.user,
            },
          });
        }
        toast.success(
          "E-mail verifie ! Vous pouvez maintenant vous connecter.",
        );
        return { success: true, message: data.message };
      } catch (error) {
        return {
          success: false,
          error:
            error.response?.data?.error ||
            "La verification de l'e-mail a echoue",
        };
      }
    },
    [setPendingVerificationEmail, dispatch],
  );

  const value = {
    ...state,
    login,
    loginWith2FA,
    register,
    logout,
    updateUser,
    changePassword,
    forgotPassword,
    resetPassword,
    resendVerification,
    verifyEmailToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
